import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import {
  Activity,
  ArrowUpRight,
  Layers,
  PieChart,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchKpiResponse,
  type WorkbenchKpiTile,
  type WorkbenchPersonaLanesResponse,
  type WorkbenchPersonaTimeMetric,
  type WorkbenchPersonaTimeResponse,
} from '@/services/api';
import { useCreativeWorkbench } from '../../pages/creative/CreativeWorkbenchContext';

// ---------------------------------------------------------------------------
// Visual constants
// ---------------------------------------------------------------------------

const SERIES_PALETTE = [
  '#14b8a6', // teal
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#f59e0b', // amber
  '#0ea5e9', // sky
  '#84cc16', // lime
  '#fb7185', // coral
  '#a855f7', // purple
];

const UNCATEGORIZED_COLOR = '#94a3b8';

function colorFor(persona: string, idx: number): string {
  if (persona.toLowerCase() === 'uncategorized') return UNCATEGORIZED_COLOR;
  return SERIES_PALETTE[idx % SERIES_PALETTE.length];
}

// ---------------------------------------------------------------------------
// Toggle option types
// ---------------------------------------------------------------------------

type ChartMode = 'trend' | 'mix' | 'lanes';

const CHART_MODES: { key: ChartMode; label: string; icon: JSX.Element }[] = [
  { key: 'trend', label: 'Trend', icon: <Activity size={11} /> },
  { key: 'mix', label: 'Mix', icon: <PieChart size={11} /> },
  { key: 'lanes', label: 'Lanes', icon: <Layers size={11} /> },
];

const METRICS: { key: WorkbenchPersonaTimeMetric; label: string }[] = [
  { key: 'spend', label: 'Spend' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'ltv_revenue', label: 'Revenue' },
  { key: 'cac', label: 'CAC' },
  { key: 'ltv_roas', label: 'ROAS' },
];

// ---------------------------------------------------------------------------
// Number formatting
// ---------------------------------------------------------------------------

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function formatMetric(value: number, metric: WorkbenchPersonaTimeMetric): string {
  if (Number.isNaN(value)) return '—';
  if (metric === 'spend' || metric === 'ltv_revenue' || metric === 'cac') {
    return CURRENCY.format(value);
  }
  if (metric === 'ltv_roas') return `${value.toFixed(2)}x`;
  return value.toLocaleString();
}

function formatKpiValue(value: number, format: string): string {
  if (Number.isNaN(value)) return '—';
  if (format === 'percent') return `${value.toFixed(1)}%`;
  if (format === 'ratio') return `${value.toFixed(2)}x`;
  if (format === 'ratio_delta') {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}x`;
  }
  if (format === 'currency') return CURRENCY.format(value);
  return value.toLocaleString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface StrategyPerformanceHeroProps {
  /** Drill-through callback when a user clicks the chart or a KPI. */
  onDrillDown?: () => void;
}

export function StrategyPerformanceHero({ onDrillDown }: StrategyPerformanceHeroProps) {
  const navigate = useNavigate();
  const { clientUid, personaId, dateRange } = useCreativeWorkbench();

  const [chartMode, setChartMode] = useState<ChartMode>('trend');
  const [metric, setMetric] = useState<WorkbenchPersonaTimeMetric>('spend');
  const [alignedOnly, setAlignedOnly] = useState(false);

  const [timeData, setTimeData] = useState<WorkbenchPersonaTimeResponse | null>(null);
  const [lanesData, setLanesData] = useState<WorkbenchPersonaLanesResponse | null>(null);
  const [kpis, setKpis] = useState<WorkbenchKpiResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch persona time series — used by Trend and Mix views.
  useEffect(() => {
    if (!clientUid) {
      setTimeData(null);
      return;
    }
    if (chartMode === 'lanes') return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getPersonaTimeSeries({
        clientUid,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        metric,
        alignedOnly,
      })
      .then((p) => {
        if (!cancelled) setTimeData(p);
      })
      .catch(() => {
        if (!cancelled) setTimeData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, dateRange.from, dateRange.to, metric, alignedOnly, chartMode]);

  // Fetch persona lanes — used by Lanes view.
  useEffect(() => {
    if (!clientUid || chartMode !== 'lanes') return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getPersonaLanes({ clientUid })
      .then((p) => {
        if (!cancelled) setLanesData(p);
      })
      .catch(() => {
        if (!cancelled) setLanesData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, chartMode]);

  // KPI strip — always loaded so the footer reflects current window.
  useEffect(() => {
    if (!clientUid) {
      setKpis(null);
      return;
    }
    let cancelled = false;
    creativeWorkbenchApi
      .getPerformanceKpis({
        clientUid,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        personaId: personaId ?? null,
      })
      .then((p) => {
        if (!cancelled) setKpis(p);
      })
      .catch(() => {
        if (!cancelled) setKpis(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, personaId, dateRange.from, dateRange.to]);

  // -------------------------------------------------------------------------
  // Trend chart rows: { date, persona1, persona2, ... }
  // -------------------------------------------------------------------------

  const trendRows = useMemo(() => {
    if (!timeData) return [];
    return timeData.points.map((p) => {
      const row: Record<string, string | number> = { date: p.date };
      for (const persona of timeData.personas) row[persona] = 0;
      for (const entry of p.by) row[entry.label] = entry.value;
      return row;
    });
  }, [timeData]);

  // Persona totals across the window, sorted desc.
  const personaTotals = useMemo(() => {
    if (!timeData) return [];
    const m = new Map<string, number>();
    for (const p of timeData.points) {
      for (const e of p.by) m.set(e.label, (m.get(e.label) ?? 0) + e.value);
    }
    return Array.from(m.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([persona, value]) => ({ persona, value }));
  }, [timeData]);

  const grandTotal = personaTotals.reduce((s, p) => s + p.value, 0);

  // Period-over-period delta — first-half vs second-half average.
  // Cheap proxy for momentum without a second API call.
  const momentum = useMemo(() => {
    if (!timeData || timeData.points.length < 4) return null;
    const points = timeData.points;
    const half = Math.floor(points.length / 2);
    const sumPart = (slice: typeof points) =>
      slice.reduce((s, p) => s + p.total, 0) / Math.max(slice.length, 1);
    const earlier = sumPart(points.slice(0, half));
    const later = sumPart(points.slice(half));
    if (earlier === 0) return null;
    const pct = ((later - earlier) / earlier) * 100;
    return { pct, direction: pct >= 0 ? 'up' : ('down' as const) };
  }, [timeData]);

  // -------------------------------------------------------------------------
  // KPI footer (always 4 tiles, gracefully degrades to placeholders)
  // -------------------------------------------------------------------------

  const kpiTiles: WorkbenchKpiTile[] = useMemo(() => {
    const all = kpis?.tiles ?? [];
    // Pick the four most strategic tiles for the hero footer.
    const wantedKeys = [
      'aligned_spend_pct',
      'steering_accuracy',
      'aligned_roas_lift',
      'ltv_weighted_roas',
    ];
    const byKey = new Map(all.map((t) => [t.key, t]));
    const picked = wantedKeys
      .map((k) => byKey.get(k))
      .filter((t): t is WorkbenchKpiTile => Boolean(t));
    return picked.length ? picked : PLACEHOLDER_TILES;
  }, [kpis]);

  const handleKpiClick = (tile: WorkbenchKpiTile) => {
    if (onDrillDown) onDrillDown();
    if (tile.drill_down === 'steering') navigate('/creative/performance/steering');
    else if (tile.drill_down === 'coverage') navigate('/creative/performance/coverage');
    else navigate('/creative/performance');
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (!clientUid) {
    return <EmptyHero />;
  }

  return (
    <section
      className={clsx(
        'rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden',
        'bg-gradient-to-br from-brand-teal/5 via-white to-brand-purple/5',
      )}
    >
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-3 px-4 pt-3 pb-2">
        <div className="flex items-center gap-1.5 mr-1">
          <Sparkles size={14} className="text-brand-teal" />
          <h2 className="text-sm font-bold text-navy-900">
            Persona-Aligned Performance
          </h2>
        </div>

        <span className="text-[10px] font-mono uppercase tracking-wide text-slate-400">
          {dateRange.window} · {personaTotals.length || (lanesData?.lanes.length ?? 0)} personas
        </span>

        {momentum && chartMode !== 'lanes' && (
          <span
            className={clsx(
              'inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full border',
              momentum.direction === 'up'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-rose-200 bg-rose-50 text-rose-700',
            )}
            title="Second-half vs first-half average in the current window"
          >
            {momentum.direction === 'up' ? (
              <TrendingUp size={10} />
            ) : (
              <TrendingDown size={10} />
            )}
            {momentum.pct >= 0 ? '+' : ''}
            {momentum.pct.toFixed(1)}%
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {/* Chart-type toggle */}
          <PillGroup
            options={CHART_MODES.map((m) => ({
              value: m.key,
              label: m.label,
              icon: m.icon,
            }))}
            value={chartMode}
            onChange={(v) => setChartMode(v as ChartMode)}
          />

          {/* Aligned-only — only meaningful for trend/mix */}
          {chartMode !== 'lanes' && (
            <label className="flex items-center gap-1 text-[10px] font-mono text-slate-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={alignedOnly}
                onChange={(e) => setAlignedOnly(e.target.checked)}
                className="w-3 h-3 accent-teal-500"
              />
              Aligned only
            </label>
          )}

          <button
            type="button"
            onClick={() => navigate('/creative/performance')}
            className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500 hover:text-teal-700 transition-colors"
          >
            Open Performance
            <ArrowUpRight size={11} />
          </button>
        </div>
      </div>

      {/* Metric toggle row — only for trend & mix */}
      {chartMode !== 'lanes' && (
        <div className="flex items-center gap-2 px-4 pb-2">
          <span className="text-[10px] font-mono uppercase tracking-wide text-slate-400">
            Metric
          </span>
          <PillGroup
            options={METRICS.map((m) => ({ value: m.key, label: m.label }))}
            value={metric}
            onChange={(v) => setMetric(v as WorkbenchPersonaTimeMetric)}
            tone="muted"
          />
        </div>
      )}

      {/* Chart body */}
      <div className="px-2 pb-3 h-44">
        {loading && !timeData && !lanesData && (
          <div className="text-xs text-slate-400 h-full flex items-center justify-center">
            Loading...
          </div>
        )}
        {chartMode === 'trend' && (
          <TrendView
            rows={trendRows}
            personas={timeData?.personas ?? []}
            metric={metric}
            empty={!loading && trendRows.length === 0}
          />
        )}
        {chartMode === 'mix' && (
          <MixView
            totals={personaTotals}
            grandTotal={grandTotal}
            metric={metric}
            empty={!loading && personaTotals.length === 0}
          />
        )}
        {chartMode === 'lanes' && (
          <LanesView
            lanes={lanesData?.lanes ?? []}
            empty={!loading && (lanesData?.lanes.length ?? 0) === 0}
            onPersonaClick={(id) => navigate(`/creative/performance/persona/${id}`)}
          />
        )}
      </div>

      {/* Persona legend — sits between chart and KPI footer */}
      {chartMode !== 'lanes' && personaTotals.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 px-4 pb-2 border-b border-slate-100">
          {personaTotals.slice(0, 6).map((t, idx) => (
            <span
              key={t.persona}
              className="inline-flex items-center gap-1.5 text-[10px] font-mono text-slate-500"
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: colorFor(t.persona, idx) }}
              />
              <span className="text-slate-600">{t.persona}</span>
              <span className="text-navy-900 font-bold">
                {formatMetric(t.value, metric)}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Mini KPI footer */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 border-t border-slate-100">
        {kpiTiles.map((tile) => (
          <button
            key={tile.key}
            onClick={() => handleKpiClick(tile)}
            className="group bg-white px-3 py-2 text-left hover:bg-teal-50/40 transition-colors"
          >
            <div className="flex items-center justify-between text-slate-400 group-hover:text-teal-500 mb-1">
              <span className="text-[9px] font-mono uppercase tracking-wide text-slate-500">
                {tile.label}
              </span>
              <KpiIcon kpi={tile.key} />
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-base font-bold text-navy-900 tabular-nums">
                {formatKpiValue(tile.value, tile.format)}
              </span>
              {tile.format === 'ratio_delta' && tile.value !== 0 && (
                <DeltaArrow value={tile.value} />
              )}
            </div>
            <p className="text-[9px] text-slate-500 mt-0.5 line-clamp-1" title={tile.subtitle}>
              {tile.subtitle}
            </p>
          </button>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Trend view — stacked area by persona
// ---------------------------------------------------------------------------

function TrendView({
  rows,
  personas,
  metric,
  empty,
}: {
  rows: Record<string, string | number>[];
  personas: string[];
  metric: WorkbenchPersonaTimeMetric;
  empty: boolean;
}) {
  if (empty) {
    return (
      <div className="text-xs text-slate-400 h-full flex items-center justify-center">
        No persona-attributed performance in this window.
      </div>
    );
  }
  if (!rows.length) return null;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={rows} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
        <defs>
          {personas.map((persona, idx) => {
            const c = colorFor(persona, idx);
            return (
              <linearGradient
                key={persona}
                id={`hero-grad-${idx}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={c} stopOpacity={0.5} />
                <stop offset="100%" stopColor={c} stopOpacity={0.05} />
              </linearGradient>
            );
          })}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} tickLine={false} axisLine={false} />
        <YAxis stroke="#94a3b8" fontSize={9} tickLine={false} axisLine={false} width={32} />
        <Tooltip
          wrapperClassName="text-xs"
          contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11 }}
          formatter={(value: number, name: string) => [formatMetric(value, metric), name]}
        />
        <Legend wrapperStyle={{ display: 'none' }} />
        {personas.map((persona, idx) => (
          <Area
            key={persona}
            type="monotone"
            dataKey={persona}
            stackId="persona"
            stroke={colorFor(persona, idx)}
            strokeWidth={1.25}
            fill={`url(#hero-grad-${idx})`}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Mix view — horizontal stacked share bar
// ---------------------------------------------------------------------------

function MixView({
  totals,
  grandTotal,
  metric,
  empty,
}: {
  totals: { persona: string; value: number }[];
  grandTotal: number;
  metric: WorkbenchPersonaTimeMetric;
  empty: boolean;
}) {
  if (empty || grandTotal === 0) {
    return (
      <div className="text-xs text-slate-400 h-full flex items-center justify-center">
        No persona attribution for this window.
      </div>
    );
  }
  return (
    <div className="h-full flex flex-col justify-center px-3 gap-3">
      <div className="flex w-full h-7 rounded-md overflow-hidden border border-slate-200 bg-white">
        {totals.map((t, idx) => {
          const pct = (t.value / grandTotal) * 100;
          return (
            <div
              key={t.persona}
              className="h-full first:rounded-l last:rounded-r relative group"
              style={{ width: `${pct}%`, background: colorFor(t.persona, idx) }}
              title={`${t.persona} · ${pct.toFixed(1)}% · ${formatMetric(t.value, metric)}`}
            >
              {pct >= 8 && (
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono text-white/95">
                  {pct.toFixed(0)}%
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
        {totals.slice(0, 6).map((t, idx) => {
          const pct = (t.value / grandTotal) * 100;
          return (
            <div key={t.persona} className="flex items-center gap-1.5 text-[11px]">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: colorFor(t.persona, idx) }}
              />
              <span className="truncate text-slate-600">{t.persona}</span>
              <span className="ml-auto font-mono tabular-nums text-navy-900 font-bold">
                {pct.toFixed(0)}%
              </span>
              <span className="font-mono tabular-nums text-slate-400">
                {formatMetric(t.value, metric)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lanes view — compact persona lanes with alignment %
// ---------------------------------------------------------------------------

function LanesView({
  lanes,
  empty,
  onPersonaClick,
}: {
  lanes: {
    persona_id: string | null;
    persona_name: string;
    spend: number;
    ad_count: number;
    aligned_spend_pct: number;
    ltv_roas: number;
  }[];
  empty: boolean;
  onPersonaClick: (id: string) => void;
}) {
  if (empty) {
    return (
      <div className="text-xs text-slate-400 h-full flex items-center justify-center">
        No active persona lanes yet.
      </div>
    );
  }
  const total = lanes.reduce((s, l) => s + l.spend, 0) || 1;
  return (
    <div className="h-full overflow-y-auto px-3 py-1">
      <ul className="divide-y divide-slate-100">
        {lanes.slice(0, 6).map((lane, idx) => {
          const sharePct = (lane.spend / total) * 100;
          const clickable = !!lane.persona_id;
          return (
            <li key={lane.persona_name} className="py-1.5">
              <button
                disabled={!clickable}
                onClick={() => lane.persona_id && onPersonaClick(lane.persona_id)}
                className="w-full text-left disabled:cursor-default group"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-xs text-navy-900 font-medium truncate">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: colorFor(lane.persona_name, idx) }}
                    />
                    {lane.persona_name}
                  </span>
                  <span className="text-[10px] font-mono text-slate-500 tabular-nums">
                    {sharePct.toFixed(0)}%
                  </span>
                </div>
                <div className="mt-1 h-1 bg-slate-100 rounded overflow-hidden">
                  <div
                    className="h-full"
                    style={{
                      width: `${sharePct}%`,
                      background: colorFor(lane.persona_name, idx),
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-[9px] font-mono text-slate-400 mt-0.5">
                  <span>
                    {CURRENCY.format(lane.spend)} · {lane.ad_count} ads
                  </span>
                  <span className="text-teal-600">
                    aligned {lane.aligned_spend_pct.toFixed(0)}% · ROAS{' '}
                    {lane.ltv_roas.toFixed(2)}x
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state when no client is selected
// ---------------------------------------------------------------------------

function EmptyHero() {
  return (
    <section className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/40 px-6 py-10 text-center">
      <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-brand-teal/15 to-brand-purple/15 mb-3">
        <Sparkles size={16} className="text-brand-teal" />
      </div>
      <h2 className="text-sm font-bold text-navy-900 mb-1">
        Persona-Aligned Performance
      </h2>
      <p className="text-xs text-slate-500 max-w-md mx-auto">
        Pick a client in the header to see persona-attributed performance over
        time, mix share, and steering accuracy at a glance.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function PillGroup<T extends string>({
  options,
  value,
  onChange,
  tone = 'accent',
}: {
  options: { value: T; label: string; icon?: JSX.Element }[];
  value: T;
  onChange: (v: T) => void;
  tone?: 'accent' | 'muted';
}) {
  return (
    <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden bg-white">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={clsx(
              'inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wide transition-colors',
              active
                ? tone === 'accent'
                  ? 'bg-teal-500 text-white'
                  : 'bg-slate-800 text-white'
                : 'bg-white text-slate-500 hover:bg-slate-50',
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function DeltaArrow({ value }: { value: number }) {
  if (value === 0) return null;
  const Icon = value > 0 ? TrendingUp : TrendingDown;
  const color = value > 0 ? 'text-emerald-500' : 'text-rose-500';
  return <Icon size={12} className={color} />;
}

function KpiIcon({ kpi }: { kpi: string }) {
  switch (kpi) {
    case 'aligned_spend_pct':
      return <Sparkles size={11} />;
    case 'steering_accuracy':
      return <Target size={11} />;
    case 'aligned_roas_lift':
      return <TrendingUp size={11} />;
    case 'ltv_weighted_roas':
      return <TrendingUp size={11} />;
    default:
      return <Users size={11} />;
  }
}

const PLACEHOLDER_TILES: WorkbenchKpiTile[] = [
  {
    key: 'aligned_spend_pct',
    label: 'Aligned Spend',
    value: 0,
    format: 'percent',
    subtitle: 'Loading...',
    drill_down: 'scatter',
  },
  {
    key: 'steering_accuracy',
    label: 'Steering Accuracy',
    value: 0,
    format: 'percent',
    subtitle: 'Loading...',
    drill_down: 'steering',
  },
  {
    key: 'aligned_roas_lift',
    label: 'Aligned ROAS Lift',
    value: 0,
    format: 'ratio_delta',
    subtitle: 'Loading...',
    drill_down: 'scatter',
  },
  {
    key: 'ltv_weighted_roas',
    label: 'LTV ROAS',
    value: 0,
    format: 'ratio',
    subtitle: 'Loading...',
    drill_down: 'leaderboard',
  },
];
